import axiosClient from '@/common/AxiosClient';
import { CEVENTAS_PROVIDER_ID, ENROLLMENT_STATUS } from '@/constants';
import { IStudentCoursesResponse } from '@/hooks/useGetStudentCourses';
import { IStudentResponse } from '@/types/views/student.types';
import {
  ITrainingCourseFiltersResponse,
  ITrainingCourseResponse,
} from '@/types/views/trainingCourse.types';
import { isCompletedCourseStatus } from '@/utilities';
import {
  MyTeamCatalogCourse,
  MyTeamCompletedCourse,
  MyTeamStudent,
  MyTeamStudentCompletions,
  buildMyTeamDataset,
} from '../datasets/myTeam';
import { PreviewDataset } from '../preview.types';

// Fetches the authenticated training manager's OWN students + their completed courses and turns
// them into the my-team PreviewDataset.
//
// CRITICAL: this runs in the UN-ARMED bootstrap phase (before the mock is armed). At that point the
// axios request interceptor injects the real B2C token and the real network adapter runs, so these
// are ordinary authenticated reads of data the TM already sees on the Training-Manager page. Once
// the caller arms the mock with the returned dataset, every further request fails closed (ADR 0001
// Amendment 1). Read-only — no writes.

// v1 targets free/trial orgs, whose teams are small. Paid orgs (deferred) will need real pagination
// and request batching here rather than a single page + one request per student.
const PAGE_SIZE = 100;
// The Training tab shows a whole course catalogue ("similar to what's real"). Bounded to one page
// so a large catalogue doesn't bloat the demo payload — plenty for a populated tab.
const CATALOG_PAGE_SIZE = 250;

// Prefer the org's OWN integration (LearnUpon/HAS/Manual) so the demo shows their real courses;
// return undefined when the org has no integration of its own, so the catalogue read falls back to
// the global Ceventas marketplace. Best-effort: a filters failure must not cost the whole demo, so
// we swallow it and default to the marketplace. The Ceventas provider is always present in the list
// (its id is CEVENTAS_PROVIDER_ID); any other provider is one the org added itself.
async function resolveOwnIntegrationProviderId(): Promise<string | undefined> {
  try {
    const res = await axiosClient.get<ITrainingCourseFiltersResponse>(
      '/TrainingManager/courses/filters'
    );
    const own = (res.data.filters?.providers ?? []).find(
      (p) => p.integrationId && p.integrationId !== CEVENTAS_PROVIDER_ID
    );
    return own?.integrationId ?? undefined;
  } catch {
    return undefined;
  }
}

export async function fetchMyTeamDataset(orgName: string): Promise<PreviewDataset> {
  // 1) The students — names only (the only PII the my-team view renders).
  const studentsRes = await axiosClient.get<IStudentResponse>('/TrainingManager/students', {
    params: { pageNumber: 1, pageSize: PAGE_SIZE },
  });
  const students: MyTeamStudent[] = (studentsRes.data.students?.data ?? []).map((s) => ({
    employeeId: s.employeeId,
    firstName: s.firstName,
    lastName: s.lastName,
  }));

  // 2) The org's course catalogue — these become the Training-tab rows. Scope to the org's own
  //    integration when it has one; otherwise the global marketplace (providerId undefined).
  const providerId = await resolveOwnIntegrationProviderId();
  const catalogRes = await axiosClient.get<ITrainingCourseResponse>('/TrainingManager/courses', {
    params: { pageNumber: 1, pageSize: CATALOG_PAGE_SIZE, providerId },
  });
  const catalog: MyTeamCatalogCourse[] = (catalogRes.data.courses?.data ?? []).map((c) => ({
    id: c.id,
    sku: c.sku,
    courseName: c.title,
    integrationAbbreviation: c.integrationName,
  }));

  // 3) Each student's COMPLETED courses. statusFilter narrows server-side; we re-check defensively.
  const completions: MyTeamStudentCompletions[] = await Promise.all(
    students.map(async (s) => {
      const res = await axiosClient.get<IStudentCoursesResponse>(
        `/TrainingManager/students/${s.employeeId}/courses`,
        // Same scope as the catalogue read above: pass the org's own integration provider so the results
        // match that catalogue rather than spanning every provider (a null providerId now means all).
        {
          params: {
            statusFilter: ENROLLMENT_STATUS.COMPLETED,
            pageNumber: 1,
            pageSize: PAGE_SIZE,
            providerId,
          },
        }
      );
      const completedCourses: MyTeamCompletedCourse[] = (res.data.courses?.data ?? [])
        .filter((c) => isCompletedCourseStatus(c.status))
        .map((c) => ({
          courseId: c.courseId ?? undefined,
          courseSourceId: c.courseSourceId,
          courseName: c.courseName,
        }));
      return { employeeId: s.employeeId, completedCourses };
    })
  );

  return buildMyTeamDataset(students, completions, catalog, orgName);
}
